import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useT } from "@/context/LanguageContext";
import { trackPageView } from "@/lib/api";

/**
 * useVisitorTracker
 *
 * Fires a fire-and-forget POST to `/api/track` every time the route
 * changes. Session identifier lives in sessionStorage so it dies with
 * the tab (one "session" == one tab). No cookies. All shopper failures
 * silently swallowed by the api helper — analytics must NEVER disrupt
 * the storefront.
 *
 * Skips:
 *   • the `/admin` sub-tree (we don't want to log our own admin edits)
 *   • duplicate consecutive fires for the same path (React StrictMode
 *     double-render, hot-reload triggers, etc.)
 */
const SESSION_KEY = "gl_visitor_session";

const mintSessionId = () => {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const fresh = `s-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
    sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch (_e) {
    // Private-mode Safari can throw on sessionStorage; degrade gracefully.
    return "s-anon";
  }
};

export default function useVisitorTracker() {
  const { pathname } = useLocation();
  const { lang } = useT();
  const lastRef = useRef("");

  useEffect(() => {
    if (!pathname) return;
    // Don't track admin routes — they're not shopper traffic.
    if (pathname.startsWith("/admin")) return;
    // Deduplicate consecutive same-path fires.
    if (lastRef.current === pathname) return;
    lastRef.current = pathname;

    trackPageView({
      path: pathname,
      referrer: document.referrer || "",
      lang,
      session_id: mintSessionId(),
    });
  }, [pathname, lang]);
}
